// Arcade hall opening screen: the narrated background story, then the choice
// of how to move (guided tour by default, or free walk with the controls
// explained). Rendered by ArcadeTv when its config carries `welcome`.

import { useEffect, useMemo, useState } from "react";
import type { ArcadeTvConfig } from "../../scenario/types";
import { audio } from "../../lib/audio";
import { useSim } from "../../state/store";
import { arcadeSfx } from "./sfx";
import { CrtOverlay, bigRects, useViewport } from "./Kit";
import {
  GAME_ORDER,
  arcadeScenarioId,
  loadProgress,
  nextTarget,
  setMoveMode,
  startRun,
  type ArcadeMoveMode,
} from "./results";
import "./arcade.css";
import "./welcome.css";
import { tx, txr } from "../../lib/tx";

type Welcome = NonNullable<ArcadeTvConfig["welcome"]>;

export function ArcadeWelcome({ welcome, voice }: { welcome: Welcome; voice: string }) {
  const sid = arcadeScenarioId();
  const vp = useViewport();
  const { glass } = bigRects(vp, 16 / 9);
  const [mode, setMode] = useState<ArcadeMoveMode>("guided");
  const [shown, setShown] = useState(1);
  const saved = useMemo(() => {
    const p = loadProgress(sid);
    const played = GAME_ORDER.filter((g) => p.results[g]).length;
    return p.briefed || played > 0 ? { played } : null;
  }, [sid]);

  useEffect(() => {
    for (const l of welcome.lines) void audio.prefetchSpeech(l, { voice });
    let alive = true;
    (async () => {
      for (let i = 0; i < welcome.lines.length && alive; i++) {
        setShown(i + 1);
        await audio.speak(welcome.lines[i], { voice });
      }
    })();
    return () => {
      alive = false;
      audio.cancelSpeech();
    };
  }, [welcome, voice]);

  const go = (resume: boolean) => {
    arcadeSfx.unlock();
    arcadeSfx.coin();
    audio.cancelSpeech();
    if (resume) setMoveMode(sid, mode);
    else startRun(sid, mode);
    useSim.getState().advance(mode === "guided" ? nextTarget(loadProgress(sid)) : "free");
  };

  return (
    <div className="arc-root arc-wel-root">
      <div className="arc-glass arc-wel" style={{ left: glass.left, top: glass.top, width: glass.width, height: glass.height }}>
        <div className="arc-wel__kicker">{welcome.kicker ?? tx("YOUR DAY OFF")}</div>
        <div className="arc-wel__title">{welcome.title}</div>
        <div className="arc-wel__story">
          {welcome.lines.map((l, i) => (
            <p key={l} className={i < shown ? "is-on" : ""}>
              {l}
            </p>
          ))}
        </div>

        <div className="arc-wel__q">{tx("HOW DO YOU WANT TO MOVE?")}</div>
        <div className="arc-wel__modes" role="radiogroup" aria-label={tx("How do you want to move?")}>
          <button
            type="button"
            role="radio"
            aria-checked={mode === "guided"}
            className={`arc-wel__mode${mode === "guided" ? " is-on" : ""}`}
            onClick={() => {
              arcadeSfx.unlock();
              arcadeSfx.select();
              setMode("guided");
            }}
          >
            <span className="arc-wel__radio" aria-hidden="true" />
            <span className="arc-wel__mname">
              {txr("GUIDED TOUR <0>RECOMMENDED</0>", {}, [<em />])}
            </span>
            <span className="arc-wel__mtext">
              {tx("We take you to the TV first, then to each cabinet in the right order. You only click and play.")}
            </span>
          </button>
          <button
            type="button"
            role="radio"
            aria-checked={mode === "free"}
            className={`arc-wel__mode${mode === "free" ? " is-on" : ""}`}
            onClick={() => {
              arcadeSfx.unlock();
              arcadeSfx.select();
              setMode("free");
            }}
          >
            <span className="arc-wel__radio" aria-hidden="true" />
            <span className="arc-wel__mname">{tx("FREE WALK")}</span>
            <ul className="arc-wel__keys">
              <li>
                {txr("<0>Walk:</0> <1>W</1> <2>A</2> <3>S</3> <4>D</4> or the arrow keys", {}, [<b />, <kbd />, <kbd />, <kbd />, <kbd />])}
              </li>
              <li>
                {txr("<0>Look around:</0> hold the left mouse button and drag", {}, [<b />])}
              </li>
              <li>
                {txr("<0>Play:</0> click the label under a glowing arrow", {}, [<b />])}
              </li>
              <li>
                {txr("<0>Start:</0> the TV straight ahead. The cabinets get their arrows after the briefing.", {}, [<b />])}
              </li>
            </ul>
          </button>
        </div>

        <div className="arc-wel__actions">
          {saved && (
            <button type="button" className="arc-btn" onClick={() => go(true)}>
              {txr("CONTINUE SAVED RUN ({played}/{length}", { played: saved.played, length: GAME_ORDER.length })})
            </button>
          )}
          <button type="button" className="arc-btn is-primary" onClick={() => go(false)}>
            {saved ? tx("START NEW RUN") : tx("START")}
          </button>
        </div>
        <CrtOverlay />
      </div>
    </div>
  );
}
